import { EditOutlined } from '@mui/icons-material';
import { Box, Button, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FieldErrors, useForm, UseFormRegister } from 'react-hook-form';

import { ProjectVenue } from '@/domain/project-venue/entities/project-venue.entity.ts';
import { updateProjectVenuePreset } from '@/domain/project-venue-presets/actions/update-project-venue-preset.action.ts';
import { VenuePreset } from '@/domain/project-venue-presets/entities/project-venue-presets.entity.ts';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton.tsx';
import { FSCard } from '@/ui/components/FSCard.tsx';
import { FSTextField } from '@/ui/components/input/FSTextField.tsx';
import { useAppDispatch } from '@/ui/hook/store.ts';
import { translate } from '@/ui/i18n';
import { AccommodationForm } from '@/ui/pages/app/project/venue/default-datas/components/forms/AccommodationForm.tsx';
import { AverageCapacityForm } from '@/ui/pages/app/project/venue/default-datas/components/forms/AverageCapacityForm.tsx';
import { EquipmentForm } from '@/ui/pages/app/project/venue/default-datas/components/forms/EquipmentForm.tsx';
import { MobilityForm } from '@/ui/pages/app/project/venue/default-datas/components/forms/MobilityForm.tsx';
import { ColorsRaw } from '@/ui/theme/colors-raw.ts';

type VenuePresetFormProps = {
  preset: VenuePreset;
  project: ProjectVenue;
};

export function VenuePresetForm({ project, preset }: VenuePresetFormProps) {
  const dispatch = useAppDispatch();

  const { reset, formState, register, watch, handleSubmit } =
    useForm<VenuePreset>({
      defaultValues: preset,
      mode: 'onBlur',
    });

  const { errors } = formState;
  const formData = watch();

  const [isEditingAForm, setIsEditingAForm] = useState(false); // this allow to lock editing while we one form is being edited

  const allowEditing = isEditingAForm === false;

  useEffect(() => {
    reset(preset);
  }, [preset, reset]);

  if (project === null) {
    return null;
  }

  const onEdit = (data: VenuePreset) => {
    reset(data); // update RHF with "data"
    handleSubmit(onSubmit)(); // validate form then call onSubmit if valid
  };

  const onChangeEditing = (isEditing: boolean) => {
    setIsEditingAForm(isEditing);
  };

  const onSubmit = async (data: VenuePreset) => {
    dispatch(updateProjectVenuePreset({ presetForm: data }));
  };

  return (
    <Box paddingX={3}>
      <TitleQuiz
        formData={formData}
        register={register}
        errors={errors}
        allowEditing={allowEditing}
        onChangeEditing={onChangeEditing}
        onSubmit={handleSubmit(onSubmit)}
      />
      <PublicJaugeQuiz
        venueDefaultDatas={formData}
        allowEditing={allowEditing}
        onChangeEditing={onChangeEditing}
        onEdit={onEdit}
      />
      <PublicTransportQuiz
        venueDefaultDatas={formData}
        allowEditing={allowEditing}
        onChangeEditing={onChangeEditing}
        onEdit={onEdit}
      />
      <PublicHebergementQuiz
        venueDefaultDatas={formData}
        allowEditing={allowEditing}
        onChangeEditing={onChangeEditing}
        onEdit={onEdit}
      />
      <TechniqueQuiz
        venueDefaultDatas={formData}
        allowEditing={allowEditing}
        onChangeEditing={onChangeEditing}
        onEdit={onEdit}
      />
    </Box>
  );
}

const TitleQuiz = ({
  formData,
  register,
  errors,
  allowEditing,
  onChangeEditing,
  onSubmit,
}: {
  formData: VenuePreset;
  register: UseFormRegister<VenuePreset>;
  errors: FieldErrors<VenuePreset>;
  allowEditing: boolean;
  onChangeEditing: (isEditing: boolean) => void;
  onSubmit: () => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const onStartEdit = () => {
    setIsEditing(true);
    onChangeEditing(true);
  };

  const onFinishEdit = () => {
    setIsEditing(false);
    onChangeEditing(false);
  };

  const onClickSubmit = () => {
    onFinishEdit();
    onSubmit();
  };

  return (
    <QuizContainer>
      {isEditing ? (
        <Box
          sx={{
            border: `1px solid ${ColorsRaw.anthracite.Anthracite_3}`,
            borderRadius: '12px',
            p: 2,
            mt: 2,
          }}
        >
          <FSTextField
            type="text"
            label={translate(`ProjectsVenue.Presets.title`)}
            register={{
              ...register('presetTitle', {
                required: translate('Form.fill-the-field'),
              }),
            }}
            error={!!errors?.presetTitle}
            helperText={errors?.presetTitle?.message}
          />
          <FSPrimaryButton onClick={onClickSubmit}>
            {translate('ProjectsVenue.Presets.save-preset')}
          </FSPrimaryButton>
        </Box>
      ) : (
        <FSCard variant="elevation" sx={{ p: 2 }}>
          <QuizTitleAndEditButton
            title={
              <FSTextField
                type="text"
                label={translate('ProjectsVenue.Presets.title')}
                value={formData.presetTitle}
                disabled={true}
              />
            }
            allowEditing={allowEditing}
            onStartEdit={onStartEdit}
          />
        </FSCard>
      )}
    </QuizContainer>
  );
};

const PublicJaugeQuiz = ({
  venueDefaultDatas,
  allowEditing,
  onChangeEditing,
  onEdit,
}: {
  venueDefaultDatas: VenuePreset;
  allowEditing: boolean;
  onChangeEditing: (isEditing: boolean) => void;
  onEdit: (venueDatas: VenuePreset) => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const onStartEdit = () => {
    setIsEditing(true);
    onChangeEditing(true);
  };

  const onFinishEdit = () => {
    setIsEditing(false);
    onChangeEditing(false);
  };

  const handleEdit = (data: VenuePreset) => {
    onFinishEdit();
    onEdit(data);
  };

  return (
    <QuizContainer>
      {isEditing ? (
        <AverageCapacityForm
          onEdit={handleEdit}
          venueDefaultDatas={venueDefaultDatas}
          forceEdit
          onClose={() => onFinishEdit()}
        />
      ) : (
        <FSCard variant="elevation" sx={{ p: 2 }}>
          <QuizTitleAndEditButton
            title={translate('VenueDates.QuizPanel.public_jauge')}
            allowEditing={allowEditing}
            onStartEdit={onStartEdit}
          />
          <AnswerTag>
            {`${venueDefaultDatas.avgCapacity} ${translate(venueDefaultDatas.avgCapacity > 1 ? 'ImpactQuestion.ACCOMMODATION_PUBLIC.people' : 'ImpactQuestion.ACCOMMODATION_PUBLIC.person')}`}
          </AnswerTag>
        </FSCard>
      )}
    </QuizContainer>
  );
};

const PublicTransportQuiz = ({
  venueDefaultDatas,
  allowEditing,
  onChangeEditing,
  onEdit,
}: {
  venueDefaultDatas: VenuePreset;
  allowEditing: boolean;
  onChangeEditing: (isEditing: boolean) => void;
  onEdit: (venueDatas: VenuePreset) => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const onStartEdit = () => {
    setIsEditing(true);
    onChangeEditing(true);
  };

  const onFinishEdit = () => {
    setIsEditing(false);
    onChangeEditing(false);
  };

  const handleEdit = (data: VenuePreset) => {
    onFinishEdit();
    onEdit(data);
  };

  return (
    <QuizContainer>
      {isEditing ? (
        <MobilityForm
          onEdit={handleEdit}
          venueDefaultDatas={venueDefaultDatas}
          forceEdit
          onClose={() => onFinishEdit()}
        />
      ) : (
        <FSCard variant="elevation" sx={{ p: 2 }}>
          <QuizTitleAndEditButton
            title={
              translate('VenueDates.QuizPanel.public') +
              ' - ' +
              translate('VenueDates.QuizPanel.transportation-mode')
            }
            allowEditing={allowEditing}
            onStartEdit={onStartEdit}
          />
          <Stack gap={1}>
            {venueDefaultDatas.mobility?.map((mobility) => (
              <AnswerTag key={mobility.type}>
                {translate(
                  `ProjectsVenue.DefaultDatas.Public.Forms.Mobility.TransportationModeValues.${mobility.type}`,
                )}
                {`: ${mobility.percentage}% - ${mobility.distance}km`}
              </AnswerTag>
            ))}
          </Stack>
        </FSCard>
      )}
    </QuizContainer>
  );
};

const PublicHebergementQuiz = ({
  venueDefaultDatas,
  allowEditing,
  onChangeEditing,
  onEdit,
}: {
  venueDefaultDatas: VenuePreset;
  allowEditing: boolean;
  onChangeEditing: (isEditing: boolean) => void;
  onEdit: (venueDatas: VenuePreset) => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const onStartEdit = () => {
    setIsEditing(true);
    onChangeEditing(true);
  };

  const onFinishEdit = () => {
    setIsEditing(false);
    onChangeEditing(false);
  };

  const handleEdit = (data: VenuePreset) => {
    onFinishEdit();
    onEdit(data);
  };

  return (
    <QuizContainer>
      {isEditing ? (
        <AccommodationForm
          onEdit={handleEdit}
          venueDefaultDatas={venueDefaultDatas}
          forceEdit
          onClose={() => onFinishEdit()}
        />
      ) : (
        <FSCard variant="elevation" sx={{ p: 2 }}>
          <QuizTitleAndEditButton
            title={
              translate('VenueDates.QuizPanel.public') +
              ' - ' +
              translate('VenueDates.QuizPanel.accommodation')
            }
            allowEditing={allowEditing}
            onStartEdit={onStartEdit}
          />
          <Stack gap={1}>
            {venueDefaultDatas.accommodation?.map((accommodation) => (
              <AnswerTag key={accommodation.type}>
                {translate(
                  `ProjectsVenue.DefaultDatas.Public.Forms.Accommodation.AccommodationTypeValues.${accommodation.type}`,
                )}
                {`: ${accommodation.percentage}%`}
              </AnswerTag>
            ))}
          </Stack>
        </FSCard>
      )}
    </QuizContainer>
  );
};

const TechniqueQuiz = ({
  venueDefaultDatas,
  allowEditing,
  onChangeEditing,
  onEdit,
}: {
  venueDefaultDatas: VenuePreset;
  allowEditing: boolean;
  onChangeEditing: (isEditing: boolean) => void;
  onEdit: (venueDatas: VenuePreset) => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const onStartEdit = () => {
    setIsEditing(true);
    onChangeEditing(true);
  };

  const onFinishEdit = () => {
    setIsEditing(false);
    onChangeEditing(false);
  };

  const handleEdit = (data: VenuePreset) => {
    onFinishEdit();
    onEdit(data);
  };

  return (
    <QuizContainer>
      {isEditing ? (
        <EquipmentForm
          onEdit={handleEdit}
          venueDefaultDatas={venueDefaultDatas}
          forceEdit
          onClose={() => onFinishEdit()}
        />
      ) : (
        <FSCard variant="elevation" sx={{ p: 2 }}>
          <QuizTitleAndEditButton
            title={translate('VenueDates.QuizPanel.technique')}
            allowEditing={allowEditing}
            onStartEdit={onStartEdit}
          />
          <Stack>
            {venueDefaultDatas.equipment?.map((equipment) => (
              <AnswerTag key={equipment.type} mt={1}>
                {`${equipment.quantity} 
                  ${translate(
                    `ProjectsVenue.DefaultDatas.Public.Forms.Equipment.EquipmentTypeValues.${equipment.type}`,
                  ).toLowerCase()}`}
              </AnswerTag>
            ))}
          </Stack>
        </FSCard>
      )}
    </QuizContainer>
  );
};

const QuizContainer = ({ children }: { children: React.ReactNode }) => {
  return <Box sx={{ mt: 2 }}>{children}</Box>;
};

const QuizTitleAndEditButton = ({
  title,
  allowEditing,
  onStartEdit,
}: {
  title: React.ReactNode;
  allowEditing: boolean;
  onStartEdit: () => void;
}) => {
  const titleElement =
    typeof title === 'string' ? (
      <Typography fontSize={20} fontWeight={'bold'}>
        {title}
      </Typography>
    ) : (
      title
    );
  return (
    <Stack direction={'row'} sx={{ minHeight: 40, gap: 2 }}>
      <Box sx={{ flexGrow: 1 }}>{titleElement}</Box>
      {allowEditing && (
        <Button sx={{ minWidth: 'fit-content' }} onClick={() => onStartEdit()}>
          <EditOutlined sx={{ color: ColorsRaw.basic.Cream }} />
        </Button>
      )}
    </Stack>
  );
};

const AnswerTag = ({
  children,
  mt,
}: {
  children: React.ReactNode;
  mt?: string | number;
}) => (
  <Box
    sx={{
      bgcolor: ColorsRaw.anthracite.Anthracite_4,
      px: 1,
      py: 0.5,
      borderRadius: '8px',
      width: 'fit-content',
    }}
    mt={mt}
  >
    <Typography fontSize={16} fontWeight={700}>
      {children}
    </Typography>
  </Box>
);
